import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Search, Tags, ShieldCheck, RefreshCw, Snowflake } from "lucide-react";
import { toast } from "sonner";
import PriceListUnitsTab from "@/pages/PriceListUnitsTab";

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const empty = () => ({ category: "Jasa", name: "", unit: "unit", price: 0, warranty_key: null, description: "", active: true });

export default function Tarif() {
  const [items, setItems] = useState([]);
  const [warranty, setWarranty] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);   // {mode:'add'|'edit', item}

  async function load() {
    setLoading(true);
    try {
      const [md, wr] = await Promise.all([api.listMasterData(), api.listWarranty()]);
      setItems(md.items || []);
      setWarranty(wr || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const cats = useMemo(() => Array.from(new Set(items.map(i => i.category))).sort(), [items]);
  const filtered = useMemo(() => items.filter(i =>
    (cat === "__all__" || i.category === cat) &&
    (!q || i.name.toLowerCase().includes(q.toLowerCase()))
  ), [items, q, cat]);

  const warrantyLabel = (key) => warranty.find(w => w.key === key)?.duration_label || (key ? key : "Tanpa Garansi");

  async function save() {
    const it = dialog.item;
    if (!it.name.trim()) { toast.error("Nama item wajib"); return; }
    if (!it.category.trim()) { toast.error("Kategori wajib"); return; }
    if (it.price < 0) { toast.error("Harga tidak valid"); return; }
    // v84.2 — Custom warranty: buat warranty_rule dulu, pakai key hasilnya
    if (dialog.addingWarranty) {
      const lbl = (dialog.customWarrantyLabel || "").trim();
      const dur = (dialog.customWarrantyDuration || "").trim();
      if (!lbl || !dur) { toast.error("Label & durasi masa garansi wajib"); return; }
      try {
        const w = await api.createWarranty({ label: lbl, duration_label: dur });
        it.warranty_key = w.key;
      } catch (e) {
        toast.error("Gagal simpan masa garansi: " + (e?.response?.data?.detail || e.message));
        return;
      }
    }
    try {
      if (dialog.mode === "add") {
        await api.createMasterItem(it);
        toast.success(`"${it.name}" ditambahkan`);
      } else {
        const { id, ...payload } = it;
        await api.updateMasterItem(id, payload);
        toast.success("Perubahan disimpan");
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  }

  async function del(item) {
    if (!window.confirm(`Hapus item "${item.name}"?`)) return;
    try {
      await api.deleteMasterItem(item.id);
      toast.success("Item dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  }

  return (
    <div className="space-y-4" data-testid="tarif-page">
      <Tabs defaultValue="services" className="space-y-4">
        <TabsList className="h-auto p-1 bg-zinc-100 rounded-sm" data-testid="tarif-tabs-list">
          <TabsTrigger value="services" className="px-4 py-1.5 text-[12px] rounded-sm data-[state=active]:bg-[#E65100] data-[state=active]:text-white"
            data-testid="tarif-tab-services">
            <Tags className="w-3.5 h-3.5 mr-1.5" />Tarif & Harga Layanan
          </TabsTrigger>
          <TabsTrigger value="units" className="px-4 py-1.5 text-[12px] rounded-sm data-[state=active]:bg-sky-600 data-[state=active]:text-white"
            data-testid="tarif-tab-units">
            <Snowflake className="w-3.5 h-3.5 mr-1.5" />Daftar Harga AC
          </TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4 mt-0" data-testid="tab-content-services">
      {/* Header */}
      <div className="bg-white border border-border rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            <Tags className="w-3 h-3 text-[#E65100]" />
            <span className="font-semibold">Tarif & Harga Layanan</span>
          </div>
          <div className="text-[13px] text-zinc-600">
            <b className="font-mono text-zinc-800">{items.length}</b> item aktif · perubahan langsung otomatis dipakai oleh
            <span className="font-semibold"> AI WhatsApp</span> untuk menjawab pertanyaan harga customer.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} className="rounded-sm gap-1.5 h-9" data-testid="tarif-refresh-btn">
            <RefreshCw className="w-4 h-4" />Refresh
          </Button>
          <Button onClick={() => setDialog({ mode: "add", item: empty() })}
            className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5 h-9" data-testid="tarif-add-btn">
            <Plus className="w-4 h-4" />Tambah Item
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input placeholder="Cari nama item…" value={q} onChange={e=>setQ(e.target.value)}
            className="rounded-sm pl-8 h-9" data-testid="tarif-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-48 rounded-sm text-[12px] h-9" data-testid="tarif-cat-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Semua Kategori ({items.length})</SelectItem>
            {cats.map(c => {
              const cnt = items.filter(i => i.category === c).length;
              return <SelectItem key={c} value={c}>{c} ({cnt})</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="tarif-table">
        {loading ? (
          <div className="p-8 text-center text-[12px] text-zinc-400">Memuat…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[12px] text-zinc-400" data-testid="tarif-empty">
            Tidak ada item yang cocok. Klik <b>Tambah Item</b> di atas.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-zinc-50 text-zinc-500 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Kategori</th>
                  <th className="text-left px-4 py-2 font-semibold">Nama Item</th>
                  <th className="text-left px-4 py-2 font-semibold">Satuan</th>
                  <th className="text-right px-4 py-2 font-semibold">Harga</th>
                  <th className="text-left px-4 py-2 font-semibold">Garansi</th>
                  <th className="text-right px-4 py-2 font-semibold w-24">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-t border-border hover:bg-zinc-50" data-testid={`tarif-row-${it.id}`}>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] font-mono bg-zinc-100 text-zinc-700 rounded-sm px-1.5 py-0.5">{it.category}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-zinc-800">{it.name}</div>
                      {it.description && <div className="text-[10px] text-zinc-500 mt-0.5 italic">{it.description}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600">{it.unit}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold text-[#E65100]" data-testid={`tarif-price-${it.id}`}>{fmtRp(it.price)}</td>
                    <td className="px-4 py-2.5">
                      {it.warranty_key ? (
                        <span className="text-[10px] flex items-center gap-1 text-emerald-700">
                          <ShieldCheck className="w-3 h-3" />{warrantyLabel(it.warranty_key)}
                        </span>
                      ) : <span className="text-[10px] text-zinc-400">Tanpa garansi</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="icon" variant="ghost" onClick={()=>setDialog({mode:"edit", item:{...it}})}
                        className="h-7 w-7 rounded-sm text-zinc-500 hover:text-[#E65100]" data-testid={`tarif-edit-${it.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={()=>del(it)}
                        className="h-7 w-7 rounded-sm text-zinc-500 hover:text-red-600" data-testid={`tarif-delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o)=>!o && setDialog(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="tarif-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "add" ? "Tambah Item Baru" : "Edit Item"}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Kategori</label>
                  {/* v84.2 — Kategori pakai Select dropdown + opsi tambah baru */}
                  <Select
                    value={cats.includes(dialog.item.category) ? dialog.item.category : "__new__"}
                    onValueChange={(v) => {
                      if (v === "__new__") {
                        setDialog({...dialog, item: {...dialog.item, category: ""}, addingCat: true});
                      } else {
                        setDialog({...dialog, item: {...dialog.item, category: v}, addingCat: false});
                      }
                    }}>
                    <SelectTrigger className="rounded-sm mt-1 h-9 text-[12px]" data-testid="tarif-form-category-select">
                      <SelectValue placeholder="Pilih kategori…" />
                    </SelectTrigger>
                    <SelectContent>
                      {cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="__new__">+ Tambah kategori baru…</SelectItem>
                    </SelectContent>
                  </Select>
                  {(dialog.addingCat || !cats.includes(dialog.item.category)) && (
                    <Input value={dialog.item.category}
                      onChange={e => setDialog({...dialog, item: {...dialog.item, category: e.target.value}})}
                      placeholder="Ketik kategori baru…" autoFocus
                      className="rounded-sm mt-1 h-9" data-testid="tarif-form-category" />
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Satuan</label>
                  <Input value={dialog.item.unit}
                    onChange={e => setDialog({...dialog, item: {...dialog.item, unit: e.target.value}})}
                    placeholder="unit / PK / meter / pcs"
                    className="rounded-sm mt-1 h-9" data-testid="tarif-form-unit" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Nama Item / Layanan</label>
                <Input value={dialog.item.name}
                  onChange={e => setDialog({...dialog, item: {...dialog.item, name: e.target.value}})}
                  placeholder="mis. Cuci AC Split 1 PK"
                  className="rounded-sm mt-1 h-9" data-testid="tarif-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Harga Satuan (Rp)</label>
                  <Input type="number" min="0" step="1000" value={dialog.item.price}
                    onChange={e => setDialog({...dialog, item: {...dialog.item, price: Number(e.target.value) || 0}})}
                    className="rounded-sm mt-1 h-9 font-mono" data-testid="tarif-form-price" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Masa Garansi</label>
                  {/* v84.2 — Tambah opsi manual utk warranty custom */}
                  <Select value={dialog.addingWarranty ? "__custom__" : (dialog.item.warranty_key || "__none__")}
                    onValueChange={v => {
                      if (v === "__custom__") {
                        setDialog({...dialog, addingWarranty: true,
                          customWarrantyLabel: "", customWarrantyDuration: "",
                          item: {...dialog.item, warranty_key: null}});
                      } else {
                        setDialog({...dialog, addingWarranty: false,
                          item: {...dialog.item, warranty_key: v === "__none__" ? null : v}});
                      }
                    }}>
                    <SelectTrigger className="rounded-sm mt-1 h-9 text-[12px]" data-testid="tarif-form-warranty"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Tanpa Garansi</SelectItem>
                      {warranty.map(w => (
                        <SelectItem key={w.key} value={w.key}>
                          {w.label} — {w.duration_label}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__">+ Tambah manual…</SelectItem>
                    </SelectContent>
                  </Select>
                  {dialog.addingWarranty && (
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      <Input value={dialog.customWarrantyLabel || ""}
                        onChange={e => setDialog({...dialog, customWarrantyLabel: e.target.value})}
                        placeholder="Label (mis. Cleaning)"
                        className="rounded-sm h-8 text-[12px]" data-testid="tarif-form-warranty-label" />
                      <Input value={dialog.customWarrantyDuration || ""}
                        onChange={e => setDialog({...dialog, customWarrantyDuration: e.target.value})}
                        placeholder="Durasi (mis. 2 minggu)"
                        className="rounded-sm h-8 text-[12px]" data-testid="tarif-form-warranty-duration" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Deskripsi Singkat (utk AI menjelaskan ke customer)</label>
                <textarea value={dialog.item.description || ""}
                  onChange={e => setDialog({...dialog, item: {...dialog.item, description: e.target.value}})}
                  placeholder="mis. Cocok utk AC ruang tamu, cuci indoor+outdoor, freon dicek."
                  rows={2} data-testid="tarif-form-description"
                  className="w-full border border-border rounded-sm p-2 mt-1 text-[12px]" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} className="flex-1 bg-[#E65100] hover:bg-[#F57C00] rounded-sm"
                  data-testid="tarif-form-save">Simpan</Button>
                <Button variant="outline" onClick={()=>setDialog(null)} className="rounded-sm">Batal</Button>
              </div>
              <div className="text-[9px] text-zinc-400 pt-1 border-t border-border">
                💡 Perubahan langsung berlaku — AI WhatsApp otomatis pakai harga terbaru di percakapan berikutnya.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="units" className="mt-0" data-testid="tab-content-units">
          <PriceListUnitsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
